import { FormikProps } from 'formik';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { AddCircle12Regular, PeopleCommunityRegular } from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { BrandCard, Column, Row } from '../../../common';
import { PartyControl } from '../../../controls/PartyControl';
import { AddAffiliatedPartiesDialog } from '../../../dialogs/AddAffiliatedPartyDialog';
import { Case, Contact, Party, PartyWithContacts } from '../../../../data/types';
import { LinkButton } from '../../../controls/LinkButton';

export const useStyles = makeStyles({
    root: {},
});
export type AffiliatedPartiesCardProps = {
    className?: string;
    caseForm?: FormikProps<Case>;
    createMode?: boolean;
    editMode?: boolean;
};
export const AffiliatedPartiesCard = (props: AffiliatedPartiesCardProps) => {
    const { className, caseForm, createMode, editMode } = props;

    //Vars
    const caseObj = caseForm?.values as Case;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);

    //Styles
    const styles = useStyles();
    const cardClasses = mergeClasses(styles.root, className);

    //Access
    const canEdit = (editable: boolean = false) => {
        if (!!createMode && access.account.canCreateCases()) {
            return true;
        }

        if (!!editMode && !!editable && access.cases.canEditCaseInfo(caseObj?.caseId)) {
            return true;
        }

        return false;
    };
    const canRemoveParty = () => {
        return access.cases.canEditCaseInfo(caseObj.caseId);
    };

    //Logic
    const onAddAffiliatedParties = (parties: Party[]) => {
        const partiesWithContacts: PartyWithContacts[] = [];
        parties.forEach((p) => {
            const pwc: PartyWithContacts = {
                party: p,
                contacts: [],
            };
            partiesWithContacts.push(pwc);
        });

        const joinedParties = [...(caseObj.affiliatedParties || []), ...partiesWithContacts];
        const distinctParties = [...new Set(joinedParties)];

        caseForm?.setFieldValue('affiliatedParties', distinctParties);
    };
    const onRemoveParty = (party: PartyWithContacts) => () => {
        const remainingParties = caseObj.affiliatedParties?.filter(
            (p) => p.party?.partyId != party.party?.partyId,
        );
        caseForm?.setFieldValue('affiliatedParties', remainingParties);
    };
    const onClientContactAddOrChange = (
        partyIndex: number,
        existingContacts: Contact[],
        addContacts: Contact[],
    ) => {
        addContacts.forEach((addContact: Contact) => {
            const contactIndex = existingContacts?.findIndex((obj) => obj.name === addContact.name);
            if (contactIndex >= 0) {
                caseForm?.setFieldValue(`affiliatedParties.${partyIndex}.contacts`, [
                    ...existingContacts.slice(0, contactIndex),
                    addContact,
                    ...existingContacts.slice(contactIndex + 1),
                ]);
            } else {
                caseForm?.setFieldValue(`affiliatedParties.${partyIndex}.contacts`, [
                    ...existingContacts,
                    addContact as Contact,
                ]);
            }
        });
    };
    const onRemoveClientContact = (partyIndex: number, remContact: Contact) => {
        const remainingContacts = caseForm?.values.affiliatedParties?.[partyIndex].contacts.filter(
            (obj: Contact) => obj.name !== remContact.name,
        );
        caseForm?.setFieldValue(`affiliatedParties.${partyIndex}.contacts`, remainingContacts);
    };

    return (
        <BrandCard
            className={cardClasses}
            title='Affiliated Parties'
            headerIcon={<PeopleCommunityRegular />}
            minHeight='fit-content'
            data-testid='case-affiliated-parties-card'
            action={
                !!canEdit(true) ? (
                    <AddAffiliatedPartiesDialog
                        affiliatedParties={caseForm?.values.affiliatedParties}
                        retainedByPartyId={caseForm?.values.retainedBy?.party?.partyId}
                        onSubmit={onAddAffiliatedParties}
                    >
                        <LinkButton
                            icon={<AddCircle12Regular />}
                            text='Add Affiliated Party'
                            data-testid='case-affiliated-parties-add-btn'
                        />
                    </AddAffiliatedPartiesDialog>
                ) : (
                    <></>
                )
            }
        >
            <Column padding='px-2 pb-2'>
                <Row gap='large'>
                    <>
                        {caseForm?.values.affiliatedParties?.map(
                            (partyWithContacts: PartyWithContacts, index: number) => (
                                <PartyControl
                                    key={partyWithContacts.party?.partyId}
                                    size='large'
                                    party={partyWithContacts.party}
                                    onRemoveParty={
                                        canRemoveParty()
                                            ? onRemoveParty(partyWithContacts)
                                            : undefined
                                    }
                                    contacts={partyWithContacts.contacts}
                                    addContacts={!!canEdit(true)}
                                    removeContacts={!!canEdit(true)}
                                    onContactsAddOrChange={(addedContacts) =>
                                        onClientContactAddOrChange(
                                            index,
                                            partyWithContacts.contacts,
                                            addedContacts,
                                        )
                                    }
                                    onContactRemoved={(remContact) =>
                                        onRemoveClientContact(index, remContact)
                                    }
                                />
                            ),
                        )}
                    </>
                </Row>
            </Column>
        </BrandCard>
    );
};
